import React from "react";
import { User } from "lucide-react";
import { isInternal, isPartner } from "utilities/userType";

function getInitials(user = {}) {
  const { firstName, lastName, name, email } = user;

  // Prefer explicit first/last
  if (firstName || lastName) {
    const a = (firstName || "").trim()[0];
    const b = (lastName || "").trim()[0];
    const str = [a, b].filter(Boolean).join("").toUpperCase();
    return str || null;
  }

  // Then full name
  if (name && typeof name === "string") {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || null;
    const first = parts[0][0];
    const last = parts[parts.length - 1][0];
    const str = [first, last].filter(Boolean).join("").toUpperCase();
    return str || null;
  }

  // Then email local part (before @), split by common separators
  if (email && typeof email === "string") {
    const local = email.split("@")[0] || "";
    const segs = local.split(/[._-]+/).filter(Boolean);
    if (segs.length === 0) return local[0]?.toUpperCase() || null;
    if (segs.length === 1) return segs[0][0]?.toUpperCase() || null;
    const str = (segs[0][0] + segs[1][0]).toUpperCase();
    return str || null;
  }

  return null;
}

const UserAvatar = ({ user = {}, isExpanded = false, size = 32 }) => {
  const displayName =
    user.name ||
    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
    user.email ||
    "User";
  const initials = getInitials(user);

  const roleLabel =
    isInternal(user)
      ? "Admin"
      : isPartner(user)
      ? "Partner"
      : "User";

  const roleBadgeClass =
    isInternal(user)
      ? "badge-destructive"
      : isPartner(user)
      ? "badge-success"
      : "badge-brand";

  const AvatarCircle = ({ size = 40 }) => (
    <div
      className="grid place-items-center rounded-full bg-muted ring-1 ring-border shadow-card overflow-hidden"
      style={{ width: size, height: size }}
      aria-label={displayName}
      title={displayName}
    >
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={displayName}
          className="h-full w-full object-cover"
        />
      ) : initials ? (
        <span
          className="font-semibold text-foreground"
          style={{ fontSize: Math.max(10, Math.floor(size * 0.42)) }}
        >
          {initials}
        </span>
      ) : (
        <User className="h-1/2 w-1/2 text-muted-foreground" />
      )}
    </div>
  );

  // Collapsed (icon-only). AvatarCircle already carries title + aria-label, so
  // the name is available on hover without a text column beside it.
  if (!isExpanded) {
    return (
      <div className="flex items-center justify-center">
        <AvatarCircle size={size} />
      </div>
    );
  }

  // Expanded (details)
  return (
    <div className="px-4 py-3 border-t border-border">
      <div className="flex items-center gap-3">
        <AvatarCircle size={40} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-sm font-medium text-foreground truncate">
              {displayName}
            </p>
            <span className={`badge ${roleBadgeClass}`}>{roleLabel}</span>
          </div>

          {user.email && (
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
          )}

          {user.companyName && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {user.companyName}
            </p>
          )}
        </div>
      </div>

      {user.featureFlag === "beta" && (
        <div className="mt-2 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-warning animate-pulse" />
          <span className="text-xs text-warning font-medium">Beta Access</span>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
